import React, { useState } from 'react';
import { useCustom, useCustomMutation, useNotification } from '@refinedev/core';
import { Alert, Button, Card, Divider, Input, InputNumber, Popconfirm, Space, Tag, Typography, theme } from 'antd';
import { ApplyReferralCodeResultDto, MemberTierDto, SetMemberTierDto } from '@wellverse/shared-ts';

/**
 * The member's tier, and the two things support can do about it: grant
 * Premium for a number of days, or put the member back on Free.
 *
 * Reads and writes RevenueCat through the API, because that is what the app
 * itself trusts. A member paying through the App Store or Google Play cannot
 * be changed from here — the API refuses and says why — so the controls are
 * replaced by that explanation rather than left to fail.
 */
export const UserMemberTier: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();
  const { open } = useNotification();
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, refetch, isError } = useCustom<MemberTierDto>({
    url: `/subscription-admin/members/${userId}/tier`,
    method: 'get',
  });
  const { mutate, isLoading: isSaving } = useCustomMutation<MemberTierDto>();

  const [code, setCode] = useState<string>('');
  const { mutate: applyCodeMutation, isLoading: isApplying } =
    useCustomMutation<ApplyReferralCodeResultDto>();

  const applyCode = () => {
    applyCodeMutation(
      {
        url: `/subscription-admin/members/${userId}/referral-code`,
        method: 'post',
        values: { code: code.trim() },
      },
      {
        onSuccess: ({ data: result }) => {
          // The code is recorded either way; the store can still refuse the
          // days (Apple does during a free trial), and support has to know.
          open?.(
            result.granted
              ? {
                  type: 'success',
                  message: `${result.code} applied: ${result.days} free days added`,
                }
              : {
                  type: 'error',
                  message: `${result.code} recorded, but the ${result.days} days were not granted`,
                  description: result.grantError ?? 'Apply the code again to retry.',
                },
          );
          if (result.granted) setCode('');
          refetch();
        },
        onError: (error) => {
          open?.({
            type: 'error',
            message: 'Could not apply the code',
            description: error?.message,
          });
        },
      },
    );
  };

  const tier = data?.data;
  const paysThroughStore = tier?.tier === 'PREMIUM' && tier.source === 'STORE';

  const setTier = (values: SetMemberTierDto) => {
    mutate(
      { url: `/subscription-admin/members/${userId}/tier`, method: 'put', values },
      {
        onSuccess: () => {
          open?.({
            type: 'success',
            message:
              values.tier === 'PREMIUM'
                ? `Premium granted for ${values.days} days`
                : 'Member set to Free',
          });
          refetch();
        },
        onError: (error) => {
          open?.({
            type: 'error',
            message: 'Could not change the tier',
            description: error?.message,
          });
        },
      },
    );
  };

  return (
    <Card title="Member Tier" size="small" loading={isLoading} style={{ marginTop: 24 }}>
      {isError ? (
        <Alert
          type="warning"
          showIcon
          message="The tier could not be read from RevenueCat."
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space size={12} wrap>
            <Tag color={tier?.tier === 'PREMIUM' ? 'gold' : 'default'}>
              {tier?.tier ?? '—'}
            </Tag>
            {tier?.tier === 'PREMIUM' && (
              <span style={{ color: token.colorTextSecondary }}>
                {tier.source === 'STORE' ? 'Paid subscription' : 'Granted'}
                {tier.expiresAt
                  ? ` · until ${new Date(tier.expiresAt).toLocaleString()}`
                  : ' · no expiry'}
              </span>
            )}
          </Space>

          {paysThroughStore ? (
            <Alert
              type="info"
              showIcon
              message="This member pays through the App Store or Google Play."
              description="Their tier follows that subscription. To end it, cancel or refund it in the store."
            />
          ) : (
            <Space size={12} wrap>
              <InputNumber
                min={1}
                max={3650}
                precision={0}
                value={days}
                onChange={(value) => setDays(value ?? 1)}
                addonAfter="days"
                style={{ width: 150 }}
              />
              <Popconfirm
                title={`Make this member Premium for ${days} days?`}
                description={
                  tier?.tier === 'PREMIUM'
                    ? 'This replaces the Premium time they have now.'
                    : undefined
                }
                onConfirm={() => setTier({ tier: 'PREMIUM', days })}
              >
                <Button type="primary" loading={isSaving}>
                  Set Premium
                </Button>
              </Popconfirm>
              {tier?.tier === 'PREMIUM' && (
                <Popconfirm
                  title="Put this member back on Free?"
                  description="Their granted Premium ends immediately."
                  onConfirm={() => setTier({ tier: 'FREE' })}
                >
                  <Button danger loading={isSaving}>
                    Set Free
                  </Button>
                </Popconfirm>
              )}
            </Space>
          )}

          <Divider style={{ margin: 0 }} />
          <Space direction="vertical" size={8}>
            <Typography.Text strong>Apply a referral code</Typography.Text>
            <Space size={12} wrap>
              <Input
                placeholder="WOOKI-XXXXXX or a promo code"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                onPressEnter={() => code.trim() && applyCode()}
                style={{ width: 260 }}
              />
              <Button loading={isApplying} disabled={!code.trim()} onClick={applyCode}>
                Apply Code
              </Button>
            </Space>
            <Typography.Text type="secondary">
              Works like entering the code on the sign-up paywall: its free days are added on top of
              the Premium time the member already has.
            </Typography.Text>
          </Space>
        </Space>
      )}
    </Card>
  );
};
